/**
 * Data-residency region for recipient links assembled in the browser (AU DR).
 *
 * The builder and the documents list build `<whiteLabelBaseUrl>/documents/v1/<referenceId>` client
 * side, so they need the same `region` param the backend puts on the links it generates. The
 * proposals API returns `region` alongside `whiteLabelBaseUrl`, and it is absent on US — so with
 * no region passed these helpers are a no-op and links keep exactly the shape they have today.
 */

export const DR_REGION_QUERY_PARAM = 'region'

/** Normalized region code. '' for US / missing, i.e. the default case. */
export const normalizeDrRegion = (region?: string | null): string => {
  const normalized = region?.trim().toLowerCase()
  return !normalized || normalized === 'us' ? '' : normalized
}

/** Appends `region=<code>` for a non-US region; returns the url untouched otherwise. Idempotent. */
export const appendRegionToUrl = (url: string, region?: string | null): string => {
  const normalized = normalizeDrRegion(region)
  if (!url || !normalized) {
    return url
  }
  if (new RegExp(`[?&]${DR_REGION_QUERY_PARAM}=`).test(url)) {
    return url
  }
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}${DR_REGION_QUERY_PARAM}=${normalized}`
}

/**
 * Regional variant of a service host for a non-US region: the first hostname label gets a
 * `-<region>` suffix, e.g.
 *   https://staging.backend.leadconnectorhq.com/emails
 *   -> https://staging-au.backend.leadconnectorhq.com/emails
 *
 * Needed for urls the browser navigates to rather than fetches — an `<iframe src>` cannot carry
 * the `x-dr-region` header, so the region has to be in the host. Returns the url untouched for
 * US / missing region, if it cannot be parsed, or if the label is already suffixed.
 */
export const applyRegionToHost = (url: string, region?: string | null): string => {
  const normalized = normalizeDrRegion(region)
  if (!url || !normalized) {
    return url
  }
  try {
    const parsed = new URL(url)
    const labels = parsed.hostname.split('.')
    if (labels.length < 2 || labels[0].endsWith(`-${normalized}`)) {
      return url
    }
    labels[0] = `${labels[0]}-${normalized}`
    parsed.hostname = labels.join('.')
    return parsed.toString().replace(/\/$/, '')
  } catch (e) {
    return url
  }
}
